import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Skeleton from '@mui/material/Skeleton'
import { useTheme } from '@mui/material/styles'
import React, { useEffect, useMemo } from 'react'

import { useLazyGetAccountDetailsQuery } from 'api/accounts/getAccountDetails'
import { useLazyGetVacationHoldListQuery } from 'api/accounts/vacationHoldsCrud'
import VacationHoldChip from 'components/common/chip/VacationHoldChip'
import { HHAlert } from 'components/common/HHAlert'
import T from 'T'
import { formatDateRangeToShortFeformat } from 'utils/date'
import { handleError } from 'utils/error'
import { combineAddressInfo } from 'utils/helper'

import VacationHoldListItem from './VacationHoldListItem'

import type { SxProps, Theme } from '@mui/material/styles'
import type { HoldsCustomerDetails } from 'components/customer-details/content/holds/types'
import type { VacationHoldApi } from 'types/api/account/vacationHold'

type Props = {
  row: { accountId: string }
  open?: boolean
  onClickVacationHoldActionMenu?: (event: React.MouseEvent<HTMLElement>, hold: VacationHoldApi) => void
  showActionMenu?: boolean
  itemSx?: SxProps<Theme>
  sx?: SxProps<Theme>
  locationId?: string | null
}

const SKELETON_ICON_SX = { minWidth: 0, mr: 1 }

const VacationHoldList: React.FC<Props> = ({
  row,
  open = false,
  onClickVacationHoldActionMenu,
  showActionMenu = false,
  itemSx,
  sx,
  locationId = null,
}) => {
  const { accountId } = row
  const theme = useTheme()
  const [getVacationHoldList, { data, isLoading, isFetching }] = useLazyGetVacationHoldListQuery()
  const [getAccountDetails, { data: accountData, isFetching: isAccountFetching, isLoading: isAccountLoading }] =
    useLazyGetAccountDetailsQuery()
  const loading = isLoading || isFetching || isAccountFetching || isAccountLoading

  const holds = useMemo(() => {
    const accountDetails: HoldsCustomerDetails | undefined = accountData
    const locationsById = new Map((accountDetails?.locations ?? []).map(location => [location.id, location]))
    return (data?.vacationHolds ?? [])
      .filter(hold => hold.active && !hold.deleted && (!locationId || hold.locations?.includes(locationId)))
      .map(hold => ({
        hold,
        dateRange: formatDateRangeToShortFeformat([hold.startDate, hold.endDate]),
        locationNames: (hold.locations ?? []).map(id => combineAddressInfo(locationsById.get(id)?.address)).filter(Boolean),
      }))
  }, [accountData, data, locationId])

  useEffect(() => {
    if (accountId && open) {
      getAccountDetails({ accountId }).unwrap().catch(handleError)
      getVacationHoldList({ accountId, deleted: false }).unwrap().catch(handleError)
    }
  }, [accountId, open, getAccountDetails, getVacationHoldList])

  return (
    <List sx={sx}>
      {loading && (
        <ListItem>
          <ListItemIcon sx={SKELETON_ICON_SX}>
            <Skeleton>
              <VacationHoldChip label="" />
            </Skeleton>
          </ListItemIcon>
          <ListItemText primary={<Skeleton variant="text" width="6rem" />} secondary={<Skeleton variant="text" width="3rem" />} />
        </ListItem>
      )}
      {!loading && holds.length === 0 && (
        <HHAlert borderColor={theme.palette.info.light} severity="info">
          {T.NO_ACTIVE_HOLDS}
        </HHAlert>
      )}
      {!loading &&
        holds.map(({ hold, dateRange, locationNames }, index) => (
          <VacationHoldListItem
            key={hold.id}
            dateRange={dateRange}
            divider={index < holds.length - 1}
            hold={hold}
            locationNames={locationNames}
            showActionMenu={showActionMenu}
            sx={itemSx}
            onOpenActionMenu={onClickVacationHoldActionMenu}
          />
        ))}
    </List>
  )
}

export default VacationHoldList
